import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Payment as PaymentIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const OverdueInstallments = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [installments, setInstallments] = useState([]);
  const [groupedInstallments, setGroupedInstallments] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInstallments();
  }, []);

  useEffect(() => {
    groupByCustomer();
  }, [installments]);

  const fetchInstallments = async () => {
    try {
      setLoading(true);
      const params = user?.userId ? { collectorId: user.userId } : {};
      console.log('Fetching overdue installments with params:', params);
      const response = await api.get('/collector/installments/overdue', { params });
      console.log('Overdue installments response:', response.data);
      setInstallments(response.data || []);
    } catch (error) {
      console.error('Error fetching installments:', error);
      console.error('Error details:', error.response?.data);
      setInstallments([]);
    } finally {
      setLoading(false);
    }
  };

  const groupByCustomer = () => {
    const filtered = installments.filter(installment => installment.installmentNumber > 1);
    const grouped = {};
    
    filtered.forEach((installment) => {
      const customer = installment.loan?.customer;
      if (!customer) return;
      
      const customerId = customer.id;
      const customerName = customer.fullName || customer.name || 'Unknown Customer';
      const customerNic = customer.nic || 'N/A';
      
      if (!grouped[customerId]) {
        grouped[customerId] = {
          customer: {
            id: customerId,
            name: customerName,
            nic: customerNic,
          },
          installments: [],
        };
      }
      
      grouped[customerId].installments.push(installment);
    });
    
    setGroupedInstallments(grouped);
  };

  const handleMakePayment = (loanId) => {
    // Navigate to the Make Payment page (FieldOfficerDashboard) with the loan ID
    navigate(`/collector/field-officer?loanId=${loanId}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'PENDING':
        return 'default';
      case 'OVERDUE':
        return 'error';
      case 'LATE':
        return 'warning';
      case 'PARTIAL':
        return 'info';
      default:
        return 'default';
    }
  };

  const totalOverdueAmount = installments
    .filter(i => i.installmentNumber > 1)
    .reduce((sum, i) => {
      const totalAmount = parseFloat(i.totalAmount) || 0;
      const paidAmount = parseFloat(i.paidAmount) || 0;
      const outstandingBalance = Math.max(0, totalAmount - paidAmount);
      return sum + outstandingBalance;
    }, 0);

  const totalOverdueCount = installments.filter(i => i.installmentNumber > 1).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Typography>Loading overdue installments...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Overdue Installments
      </Typography>
      
      {/* Summary Card */}
      <Card sx={{ mb: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6">Total Overdue Installments</Typography>
              <Typography variant="h4">{totalOverdueCount}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6">Total Overdue Amount</Typography>
              <Typography variant="h4">Rs {totalOverdueAmount.toFixed(2)}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Grouped by Customer */}
      {Object.keys(groupedInstallments).length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No overdue installments found for your assigned area(s).
          </Typography>
        </Paper>
      ) : (
        <Box>
          {Object.values(groupedInstallments).map((group) => (
            <Accordion key={group.customer.id} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                  <Box>
                    <Typography variant="h6">{group.customer.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      NIC: {group.customer.nic} | {group.installments.length} overdue installment(s)
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="error">
                    Rs {group.installments.reduce((sum, i) => {
                      const totalAmount = parseFloat(i.totalAmount) || 0;
                      const paidAmount = parseFloat(i.paidAmount) || 0;
                      const outstandingBalance = Math.max(0, totalAmount - paidAmount);
                      return sum + outstandingBalance;
                    }, 0).toFixed(2)}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Loan ID</strong></TableCell>
                        <TableCell><strong>Installment #</strong></TableCell>
                        <TableCell align="right"><strong>Base Amount</strong></TableCell>
                        <TableCell align="right"><strong>Paid Amount</strong></TableCell>
                        <TableCell align="right"><strong>Late Interest</strong></TableCell>
                        <TableCell align="right"><strong>Outstanding Balance</strong></TableCell>
                        <TableCell><strong>Due Date</strong></TableCell>
                        <TableCell align="center"><strong>Days Overdue</strong></TableCell>
                        <TableCell align="center"><strong>Status</strong></TableCell>
                        <TableCell align="center"><strong>Actions</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {group.installments.map((installment) => {
                        const dueDate = new Date(installment.dueDate);
                        const today = new Date();
                        const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
                        const baseAmount = parseFloat(installment.amount || 0);
                        const paidAmount = parseFloat(installment.paidAmount || 0);
                        const lateInterest = parseFloat(installment.lateInterest || 0);
                        const totalAmount = parseFloat(installment.totalAmount || 0);
                        // Calculate outstanding balance: totalAmount - paidAmount
                        const outstandingBalance = Math.max(0, totalAmount - paidAmount);
                        // Calculate outstanding base amount (base amount that hasn't been paid)
                        // Note: paidAmount can exceed baseAmount if there's late interest, so we need to handle this carefully
                        const outstandingBaseAmount = Math.max(0, baseAmount - Math.min(paidAmount, baseAmount));
                        
                        return (
                          <TableRow key={installment.id} hover>
                            <TableCell><strong>{installment.loan?.id}</strong></TableCell>
                            <TableCell><strong>#{installment.installmentNumber}</strong></TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                Rs {baseAmount.toFixed(2)}
                              </Typography>
                              {outstandingBaseAmount < baseAmount && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                  Outstanding: Rs {outstandingBaseAmount.toFixed(2)}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ color: paidAmount > 0 ? 'success.main' : 'text.secondary' }}>
                                Rs {paidAmount.toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {lateInterest > 0 ? (
                                <Box>
                                  <Typography variant="body2" color="error.main" sx={{ fontWeight: 700 }}>
                                    Rs {lateInterest.toFixed(2)}
                                  </Typography>
                                  {installment.loan?.loanTemplate && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      Rate: {parseFloat(installment.loan.loanTemplate.lateInterestRate || 0).toFixed(2)}%
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  Rs 0.00
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 700, color: outstandingBalance > 0 ? 'error.main' : 'success.main' }}>
                                Rs {outstandingBalance.toFixed(2)}
                              </Typography>
                              {outstandingBalance === 0 && (
                                <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                                  Fully Paid
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{dueDate.toLocaleDateString()}</TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={`${daysOverdue} days`} 
                                color={daysOverdue > 30 ? 'error' : daysOverdue > 10 ? 'warning' : 'default'}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip 
                                label={installment.status} 
                                color={getStatusColor(installment.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                startIcon={<PaymentIcon />}
                                onClick={() => handleMakePayment(installment.loan?.id)}
                              >
                                Make Payment
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OverdueInstallments;

